import React, { useEffect, useState } from "react";
import ScrollToTop from "react-scroll-to-top";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Avatar,
  Button,
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Stack,
  CardHeader,
} from "@mui/material";
import { useLocation, useNavigate } from 'react-router-dom';
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Bar, Line } from "react-chartjs-2";
import makeStyles from "@material-ui/styles/makeStyles";

import { getBookingData, getDashboardInfo } from "../../redux/actions/book";
import UserMember from "../../components/layout/UserMember";
import WelcomeModal from "../../components/layout/WelcomeModal";

import config from "../../config/config";
import AccountExpiredModal from "../../components/layout/AccountExpiredModal";
import CreateTicket from "../../components/layout/CreateTicket";
import SentTicketSuccessModal from "../../components/layout/SentTicketSuccessModal";

export const barChartOptions = {
  responsive: true,
  cornerRadius: 10,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      }
    },
  }
};

const lineChartOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  tension: 0.5,
  elements: {
    point: {
      radius: 0,
    }
  },
  scales: {
    x: {
      grid: { display: false }
    },
    y: {
      grid: { display: false }
    },
  }
}


const DashbaordPage = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [totalBookingCount, setTotalBookingCount] = useState(0);
  const [monthLabels, setMonthLabels] = useState([]);
  const [approvedBookings, setApprovedBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState([]);
  const [formVisitCount, setFormVisitCount] = useState([]);
  const [totalVisitCount, setTotalVisitCount] = useState(0);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showExpiredAccountModal, setShowExpiredAccountModal] = useState(false);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [showSentTicketSuccessModal, setSentTicketSuccessModal] = useState(false);

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    await dispatch(getBookingData());
    const res = await dispatch(getDashboardInfo());
    setMonthLabels([ ...res.clientBookings.months ]);
    setApprovedBookings([ ...res.clientBookings.approvedBookingCount ]);
    setTotalBookings([ ...res.clientBookings.totalBookingCount ]);
    setTotalBookingCount(res.clientBookings.totalCount);

    setFormVisitCount(res.formVisitCount);
    setTotalVisitCount(res.formVisitCount.reduce((a, b) => a + b, 0));
  }

  const useStyles = makeStyles({
    title: {
      color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde',
      fontSize: '14px',
      fontWeight: '700',
      lineHeight: '16px',
      marginRight: '20px',
      marginBottom: '3px',
    },
    subheader: {
      color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde',
      fontSize: '12.5px',
      lineHeight: '18px',
    }
  });
  const styles = useStyles();

  const barChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Approved',
        data: approvedBookings,
        backgroundColor: 'rgba(0, 158, 247, 0.85)',
        borderRadius: 6,
      },
      {
        label: 'Total',
        data: totalBookings,
        backgroundColor: theme.palette.mode === 'light' ? 'rgba(228, 230, 239, 0.85)' : 'rgba(50, 50, 72, 0.85)',
        borderRadius: 6,
      },
    ],
  };

  const lineChartData = {
    labels: monthLabels,
    datasets: [
      {
        fill: true,
        data: formVisitCount,
        borderColor: '#009ef7',
        backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
      }
    ]
  }

  const { loading, bookingData, user, perkData } = useSelector(state => ({
    loading: state.book.loading,
    user: state.auth.user,
    bookingData: state.book.bookingData,
    perkData: state.perk.perkData
  }));
	
  const onUpdateMembership = async (message) => {
    const data = {
      upgrade: user?.membership < 2,
      message,
    }
  }

  useEffect(() => {
    if (user?.id && location.state) {
      const dontShow = localStorage.getItem(user.id + "_DONT_SHOW_WELCOME_TIP") || "false";
      if (location.state?.showTipNow && dontShow === 'false') {
        setShowWelcomeModal(true);
      }
    }
    if (user?.membership === 1) {
      setShowExpiredAccountModal(true);
    }
  }, [user, location.state]);

  const onCloseWelcomeModal = () => setShowWelcomeModal(false);
  const onCloseExiredAccountModal = () => setShowExpiredAccountModal(false);
  const onCloseCreateTicketModal = () => setShowCreateTicketModal(false);
  const onCloseSentTicketSuccessModal = () => setSentTicketSuccessModal(false);
  
  const onSetupMyProfile = () => {
    navigate("/account-settings/profile");
  }
  const onSettings = () => {
    navigate("/account-settings/form");
  }
  const onCustomizePack = () => {
    navigate("/onboard/form");
  }
  const onLatestCampaigns = () => {
    navigate("/perks/perks-admin");
  }

  function getBackgroundAvatar() {
    if (user == null) {
      return '';
    } else if (user?.avatar?.includes("https://gravatar.com/")) {
      return `url("default.jpg")`
      // return `url(${user.avatar})`;
    } else {
      return `url(${config.SERVER_BASE_URL}/${user.avatar})`;
    }
  }

  function getDaysRemainingText(expiryDate) {
    const daysRemaining = moment(expiryDate).diff(moment(), 'days');
    return moment(expiryDate).isBefore(moment())
      ? 'Expired'
      : daysRemaining + ' Day' + (daysRemaining > 1 ? 's ' : ' ') + 'Left';
  }

  function getPendingBookCount() {
    const pendingBookData = bookingData.filter(e => e.status === 0);
    return pendingBookData.length;
  }

  function getActivePerksCount() {
    const activePerkData = perkData.filter(e => e.status === 0);
    return activePerkData.length;
  }

  const onSendTicketModal = () => {
    setShowCreateTicketModal(true);
  }
  const onCreatedTicketSuccessed = () => {
    setSentTicketSuccessModal(true);
  }
  const onCloseSentTicketSuccess = () => {
    onCloseSentTicketSuccessModal();
    onCloseCreateTicketModal();
  }

  return (
    <Container maxWidth="lg">
      <Grid
        item
        className="page-title"
        container
        justifyContent='space-between'
        sx={{ my: { sm: 2, md: 5 }, marginInline: 'auto' }}
      >
        <Grid item>
          <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>Dashboard</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500, mt: '3px', opacity: 0.75 }}>Welcome to Clientl, an all-in-one provider platform to manage your bookings, screenings, date onboarding and increase overall client conversion rate.</Typography>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
      >
        <Grid
          item
          container
          md={12}
          lg={8}
          xs={12}
          sx={{
            backgroundImage: isMd ? getBackgroundAvatar(): "",
            backgroundSize: "50% auto",
            backgroundClip: "content-box",
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
            borderRadius: 2,
          }}
        >
          <Container
            direction='column'
            sx={{
              padding: '50px 60px !important',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '50px', color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>Welcome</Typography>
            <Stack direction="row" alignItems="center">
              <Typography sx={{ fontWeight: 300, fontSize: '1.3rem', color: theme.palette.mode ==="light" ? "#181c32" : "#fff", mr: 1 }}>{user?.fullName}</Typography>
              <UserMember/>
            </Stack>
            <Typography sx={{ fontSize: '14px', color: theme.palette.mode ==="light" ? "#181c32" : "#fff", mt: 2 }}>
              You have <strong>{user?.currentMonthBookCount} new bookings</strong> and <br /><strong>{user?.bookFormVisitCount} booking form views</strong> this month.
            </Typography>
            <Typography sx={{ fontSize: '14px', color: theme.palette.mode ==="light" ? "#181c32" : "#fff", mt: 2 }}>
              {user?.membership === 2 ? 'Your Pro membership renews' : 'Your trial membership expires'}<br />on <strong>{moment(user?.expireDate).format('DD MMMM YYYY')}.</strong>
            </Typography>
            <Stack direction="row" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onSetupMyProfile}
                sx={{ color: '#fff', fontWeight: 600, textTransform: 'none' }}
              >
                Setup My Profile
              </Button>
              <Button
                onClick={onSettings}
                size="large"
                variant="contained"
                sx={{
                  color: theme.palette.mode === 'light' ? '#181c32' : '#fff',
                  marginInlineStart: 2,
                  backgroundColor: 'transparent',
                  border: '1px solid #323248',
                  borderColor: theme.palette.mode === 'light' ? '#e4e6ef' : '#323248',
                  textTransform: 'none',
                  "&:hover": {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Settings
              </Button>
            </Stack>
          </Container>
        </Grid>
        <Grid
          item
          md={12}
          lg={4}
          xs={12}
          sx={{
            minHeight: '300px',
          }}
        >
          <Stack direction="column" spacing={2}>
            <Container
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundImage: `url("/dashboard_onboarding.png")`,
                backgroundSize: "contain",
                backgroundClip: "content-box",
                backgroundPositionX: '100%',
                backgroundRepeat: 'no-repeat',
                backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
                height: '200px',
                padding: '0px !important',
                borderRadius: 2,
              }}
            >
              <Grid sx={{ maxWidth: '55%', paddingLeft: '30px' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '18px', color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>Client Onboarding</Typography>
                <Typography sx={{ fontSize: '13px', mt: 1, mb: 2, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>
                  Customize a Welcome Pack email to reduce time onboarding new clients.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={onCustomizePack}
                  sx={{ color: '#fff', fontWeight: 600, textTransform: 'none' }}
                >
                  Customize Pack
                </Button>
              </Grid>
            </Container>
            <Container
              direction='column'
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundImage: `url("/loyalty_perks.png")`,
                backgroundSize: "contain",
                backgroundClip: "content-box",
                backgroundPositionX: '100%',
                backgroundRepeat: 'no-repeat',
                backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
                height: '185px',
                // width: '95%',
                padding: '0 !important',
                borderRadius: 2,
              }}
            >
              <Grid sx={{ maxWidth: '55%', paddingLeft: '30px' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '18px', color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>Loyalty Perks</Typography>
                <Typography sx={{ fontSize: '13px', mt: 1, mb: 2, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>
                  Boost return business with personalized loyalty perks
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={onLatestCampaigns}
                  sx={{ color: '#fff', fontWeight: 600, textTransform: 'none' }}
                >
                  Create New Perks
                </Button>
              </Grid>
            </Container>
          </Stack>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{ mt: 2 }}
      >
        <Grid
          item
          md={12}
          lg={4}
          xs={12}
          sx={{
            minHeight: '300px',
          }}
        >
          <Stack direction="column" spacing={2}>
            <Container
              direction="column"
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
                padding: '0px !important',
                borderRadius: 2,
              }}
            >
              <Grid display="flex" justifyContent="space-between" sx={{ padding: '24px', width: '100%' }}>
                <Grid>
                  <Typography sx={{ fontWeight: 600, fontSize: '17px', color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Client Bookings</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: '20px', color: theme.palette.mode ==="light" ? "#b5b5c3" : "#474761" }}>
                    Past 6 months
                  </Typography>
                </Grid>
                <Grid align="right">
                  <Typography sx={{ fontWeight: 600, fontSize: '17px', color: "#009ef7" }}>{totalBookingCount} Bookings</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: '20px', color: theme.palette.mode ==="light" ? "#b5b5c3" : "#474761" }}>
                    All Time
                  </Typography>
                </Grid>
              </Grid>
              <Grid sx={{ padding: '0 30px 30px 30px' }}>
                <Bar options={barChartOptions} data={barChartData} />
              </Grid>
            </Container>
            <Container
              direction='column'
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
                padding: '0 !important',
                borderRadius: 2,
                height: '300px',
              }}
            >
              <Grid display="flex" justifyContent="space-between" sx={{ padding: '30px', width: '100%' }}>
                <Grid>
                  <Typography sx={{ fontWeight: 600, fontSize: '17px', color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Form Views</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: '20px', color: theme.palette.mode ==="light" ? "#b5b5c3" : "#474761" }}>
                    Past 6 months
                  </Typography>
                </Grid>
                <Grid align="right">
                  <Typography sx={{ fontWeight: 600, fontSize: '17px', color: "#009ef7" }}>{totalVisitCount} Views</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: '20px', color: theme.palette.mode ==="light" ? "#b5b5c3" : "#474761" }}>
                    All Time
                  </Typography>
                </Grid>
              </Grid>
              <Grid sx={{ padding: '0 20px 30px 30px' }}>
                <Line options={lineChartOptions} data={lineChartData} />
              </Grid>
            </Container>
          </Stack>
        </Grid>
        <Grid
          item
          container
          md={12}
          lg={4}
          xs={12}
        >
          <Container
            direction='column'
            sx={{
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
              padding: '30px !important',
            }}
          >
            <Grid display={"flex"} justifyContent={"space-between"}>
              <Grid>
                <Typography sx={{ fontWeight: 600, fontSize: '17px', color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Latest Bookings</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: theme.palette.mode ==="light" ? "#b5b5c3" : "#474761" }}>Total {getPendingBookCount()} Pending</Typography>
              </Grid>
              <Grid>
                <Button
                  onClick={() => navigate('/bookings')}
                  sx={{
                    backgroundColor: theme.palette.mode ==="light" ? '#f5f8fa' : '#2b2b40',
                    padding: '10px 17px',
                    color: '#6d6d80',
                    fontSize: '12px',
                    lineHeight: '18px',
                    textTransform: 'none',
                    "&:hover": {
                      backgroundColor: theme.palette.mode ==="light" ? '#eff2f5' : '#323248',
                    },
                  }}
                >
                  View All
                </Button>
              </Grid>
            </Grid>
            <Grid sx={{ mt: 2 }}>
              <TableContainer sx={{ width: '100%' }}>
                <Table aria-label="simple table">
                  <TableBody>
                    {bookingData.slice(0, 6).map((row, index) => (
                      <TableRow
                        tabIndex={-1}
                        key={index}
                        sx={{ borderBottom: "1px dashed", borderBottomColor: theme.palette.mode === 'light' ? '#eff2f5' : "#323248", '&:last-child': { borderBottom: 'none' } }}
                      >
                        <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>
                          <CardHeader
                            classes={{ title: styles.title, subheader: styles.subheader }}
                            avatar={row.selfiePhotoThumbnail ?
                              <Box
                                component="img"
                                src={`${config.SERVER_BASE_URL}/${row.selfiePhotoThumbnail}`}
                                alt="avatar"
                                style={{
                                  width: '3rem',
                                  height: '3rem',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  transform: 'translate3d(0, 0, 1px)',
                                }}
                              /> :
                              <Avatar sx={{ height: '3rem', width: '3rem' }} aria-label="recipe">
                                {row.fullName[0]}
                              </Avatar>
                            }
                            title={row.fullName}
                            subheader={row.client.occupation}
                            sx={{ padding: 0 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>
                          {
                            row.status === 0
                              ? <Box className="booking-detail-type" sx={{ color: '#009ef7', padding: '6px 13px', fontSize: '12px', lineHeight: '18px', backgroundColor: theme.palette.mode ==="light" ? "#f1faff" : "#212e48" }}>Pending</Box>
                              : (row.status === 1
                                ? <Box className="booking-detail-type" sx={{ color: '#fff', padding: '6px 13px', fontSize: '12px', lineHeight: '18px', backgroundColor: "#f1416c" }}>Declined</Box>
                                : <Box className="booking-detail-type" sx={{ color: '#fff', padding: '6px 13px', fontSize: '12px', lineHeight: '18px', backgroundColor: "#50cd89" }}>Approved</Box>)
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            {bookingData.length === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 60px)' }}>
                <Typography>There are no bookings yet.</Typography>
              </Box>
            )}
          </Container>
        </Grid>
        <Grid
          item
          container
          md={12}
          lg={4}
          xs={12}
          sx={{
          }}
        >
          <Container
            direction='column'
            sx={{
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)',
              padding: '30px !important',
            }}
          >
            <Grid display={"flex"} justifyContent={"space-between"}>
              <Grid>
                <Typography sx={{ fontWeight: 600, fontSize: '17px', color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Latest Campaigns</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: theme.palette.mode ==="light" ? "#b5b5c3" : "#474761" }}>Total {getActivePerksCount()} Active Perks</Typography>
              </Grid>
              <Grid>
                <Button
                  onClick={onLatestCampaigns}
                  sx={{
                    backgroundColor: theme.palette.mode ==="light" ? '#f5f8fa' : '#2b2b40',
                    padding: '10px 17px',
                    color: '#6d6d80',
                    fontSize: '12px',
                    lineHeight: '18px',
                    textTransform: 'none',
                    "&:hover": {
                      backgroundColor: theme.palette.mode ==="light" ? '#eff2f5' : '#323248',
                    },
                  }}
                >
                  View All
                </Button>
              </Grid>
            </Grid>
            <Grid sx={{ mt: 2 }}>
              <TableContainer sx={{ width: '100%' }}>
                <Table aria-label="simple table">
                  <TableBody>
                    {perkData.slice(0, 6).map((row, index) => (
                      <TableRow
                        tabIndex={-1}
                        key={index}
                        sx={{ borderBottom: "1px dashed", borderBottomColor: theme.palette.mode === 'light' ? '#eff2f5' : "#323248", '&:last-child': { borderBottom: 'none' } }}
                      >
                        <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>
                          <CardHeader
                            classes={{ title: styles.title, subheader: styles.subheader }}
                            avatar={
                              <Box component="img" src={`${config.SERVER_BASE_URL}/${row.featuredImage}`} sx={{ width: '75px', height: '75px', objectFit: 'cover', borderRadius: '6px', transform: 'translate3d(0, 0, 1px)' }} alt="Perk Campaign" />
                            }
                            title={row.title}
                            subheader={getDaysRemainingText(row.expiryDate)}
                            sx={{ padding: 0 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: '600', lineHeight: '18px', color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde' }}>{row.visitCount}</Typography>
                          <Typography sx={{ fontSize: '12px', fontWeight: '400', lineHeight: '18px', color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde' }}>Views</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            {perkData.length === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 60px)' }}>
                <Typography>There are no Perks yet.</Typography>
              </Box>
            )}
          </Container>
        </Grid>
      </Grid>
      <WelcomeModal
        visible={showWelcomeModal}
        onClose={onCloseWelcomeModal}
        id={user?.id}
      />
      <AccountExpiredModal
        visible={showExpiredAccountModal}
        onClose={onSendTicketModal}
      />
      <CreateTicket
        visible={showCreateTicketModal}
        onClose={onCloseCreateTicketModal}
        onCreatedTicketSuccessed={onCreatedTicketSuccessed}
      />
      <SentTicketSuccessModal
        visible={showSentTicketSuccessModal}
        onClose={onCloseSentTicketSuccessModal}
        onCloseSuccess={onCloseSentTicketSuccess}
      />
      <ScrollToTop
        smooth
        color="#009EF7"
      />
    </Container>
  );
}

export default DashbaordPage;